// Angular import
import { Component, OnInit, Renderer2, ViewEncapsulation, inject } from '@angular/core';
import { Location, LocationStrategy } from '@angular/common';
import { RouterModule } from '@angular/router';

// project import
import { BerryConfig } from 'src/app/app-config';
import { ThemeService } from 'src/app/theme/shared/service/theme.service';
import { SharedModule } from 'src/app/theme/shared/shared.module';
import { ScrollbarComponent } from 'src/app/theme/shared/components/scrollbar/scrollbar.component';

@Component({
  selector: 'app-configuration',
  imports: [SharedModule, RouterModule, ScrollbarComponent],
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ConfigurationComponent implements OnInit {
  private location = inject(Location);
  private renderer = inject(Renderer2);
  private locationStrategy = inject(LocationStrategy);
  private themeService = inject(ThemeService);

  // public method
  styleSelectorToggle!: boolean; // open configuration menu
  berryConfig!: BerryConfig; // theme Config
  windowWidth: number; // screen width
  layout!: string; // sidebar navigation
  themeMode!: boolean; // layoutMode type
  rtlLayout!: boolean; // rtl type
  boxContainer!: boolean; // box container flag
  setFontFamily!: string; // fontFamily
  bodyColor!: string; // berry Customizes
  fontSize!: string;       // font size px value
  customFontSize = '';     // custom font size input

  readonly fontSizeOptions = [
    { label: 'S',  value: '14' },
    { label: 'M',  value: '16' },
    { label: 'L',  value: '18' },
    { label: 'XL', value: '20' },
  ];
  sidebar_caption_hide!: boolean; // sidebar menu caption
  resetLayoutType!: string; // default layout
  landingPage!: boolean;
  active = 1;

  // Constructor
  constructor() {
    this.setThemeLayout();
    this.windowWidth = window.innerWidth;
  }

  // Life cycle events
  ngOnInit() {
    this.layout = BerryConfig.layout;
    this.setMenuOrientation(this.layout);
    this.styleSelectorToggle = false;
    this.themeMode = BerryConfig.isDarkMode;
    this.setDarkLayout(this.themeMode);
    this.setFontFamily = BerryConfig.font_family;
    this.fontFamily(this.setFontFamily);
    this.fontSize = BerryConfig.font_size;
    this.setFontSize(this.fontSize);
    this.bodyColor = BerryConfig.theme_color;
    this.SetBodyColor(this.bodyColor);
    this.rtlLayout = BerryConfig.isRtl_layout;
    this.setRtlLayout(this.rtlLayout);
    this.boxContainer = this.windowWidth >= 1025 ? BerryConfig.isBox_container : false;
    this.setBoxContainer(this.boxContainer);
    this.sidebar_caption_hide = BerryConfig.sidebar_caption_hide;
    this.captionShow(this.sidebar_caption_hide);
    this.landingPage = this.themeService.isLandingPage();
  }

  // public method

  // sidebar layout change
  setThemeLayout() {
    let current_url = this.location.path();
    const baseHref = this.locationStrategy.getBaseHref();
    if (baseHref) {
      current_url = baseHref + this.location.path();
    }

    switch (current_url) {
      case baseHref + '/layout/vertical':
        BerryConfig.layout = 'vertical';
        break;

      case baseHref + '/layout/compact':
        BerryConfig.layout = 'compact';
        break;

      case baseHref + '/layout/horizontal':
        BerryConfig.layout = 'horizontal';
        break;
    }
  }

  // change main layout dark and light
  setDarkLayout(isDark: boolean) {
    if (isDark) {
      this.renderer.addClass(document.body, 'berry-dark');
      document.querySelector('html')?.classList.add('dark');
      this.themeMode = true;
    } else {
      this.renderer.removeClass(document.body, 'berry-dark');
      document.querySelector('html')?.classList.remove('dark');
      this.themeMode = false;
    }
    BerryConfig.isDarkMode = this.themeMode;
    this.themeService.isDarkTheme.set(this.themeMode);
    BerryConfig.save();
  }

  // default mode
  setResetLayout(layout: string) {
    if (layout === 'reset') {
      BerryConfig.font_size = '16';
      this.customFontSize   = '';
      this.ngOnInit();
    }
  }

  // ser rtl and ltr theme mode
  setRtlLayout(layout: boolean) {
    if (layout) {
      this.renderer.addClass(document.body, 'berry-rtl');
      this.renderer.removeClass(document.body, 'berry-ltr');
      this.rtlLayout = true;
    } else {
      this.renderer.removeClass(document.body, 'berry-rtl');
      this.renderer.addClass(document.body, 'berry-ltr');
      this.rtlLayout = false;
    }
    BerryConfig.isRtl_layout = this.rtlLayout;
    this.themeService.isRtlTheme.set(this.rtlLayout);
    BerryConfig.save();
  }

  // sidebar menu caption show and hide
  captionShow(hide: boolean) {
    if (hide) {
      document.querySelector('.coded-navbar')?.classList.add('caption-hide');
      this.sidebar_caption_hide = true;
    } else {
      document.querySelector('.coded-navbar')?.classList.remove('caption-hide');
      this.sidebar_caption_hide = false;
    }
    BerryConfig.sidebar_caption_hide = this.sidebar_caption_hide;
    BerryConfig.save();
  }

  // set box container
  setBoxContainer(boxContainer: boolean) {
    if (boxContainer) {
      document.querySelector('.coded-content')?.classList.add('container');
      this.boxContainer = true;
    } else {
      document.querySelector('.coded-content')?.classList.remove('container');
      this.boxContainer = false;
    }
    BerryConfig.isBox_container = this.boxContainer;
    this.themeService.isBoxLayout.set(boxContainer);
    BerryConfig.save();
  }

  // set font size — zoom is applied to <html> so it scales ALL text (rem + px)
  setFontSize(size: string): void {
    const px = Number(size);
    if (!px || px < 10 || px > 28) return;
    this.fontSize = size;
    BerryConfig.font_size = size;
    // zoom = desired px / default 16px base
    const zoom = Math.round((px / 16) * 1000) / 1000;
    document.documentElement.style.zoom = String(zoom);
    // vh units do NOT shrink with zoom — 100vh renders at zoom × screen height.
    // Publish a pre-divided value so full-height layouts (sidebar, drawers)
    // can use var(--vh100) instead of 100vh and stay exactly screen-height.
    document.documentElement.style.setProperty('--vh100', `${Math.round((100 / zoom) * 100) / 100}vh`);
    // Charts (ApexCharts etc.) measure their container only on window resize —
    // zoom changes don't fire one, so charts keep their stale width and
    // overflow. Dispatch after layout settles so they re-measure and redraw.
    setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
    BerryConfig.save();
  }

  applyCustomFontSize(): void {
    const px = Number(this.customFontSize);
    if (!px || px < 10 || px > 28) return;
    this.setFontSize(String(px));
  }

  // set font family
  fontFamily(font: string) {
    this.setFontFamily = font;
    this.renderer.removeClass(document.body, 'Roboto');
    this.renderer.removeClass(document.body, 'Poppins');
    this.renderer.removeClass(document.body, 'Inter');
    this.renderer.addClass(document.body, font);
    BerryConfig.font_family = font;
    BerryConfig.save();
  }

  // set theme different color
  SetBodyColor(background: string) {
    this.bodyColor = background;
    document.querySelector('body')?.part.remove('preset-1');
    document.querySelector('body')?.part.remove('preset-2');
    document.querySelector('body')?.part.remove('preset-3');
    document.querySelector('body')?.part.remove('preset-4');
    document.querySelector('body')?.part.remove('preset-5');
    document.querySelector('body')?.part.remove('preset-6');
    document.querySelector('body')?.part.remove('preset-7');
    document.querySelector('body')?.part.add(background);
    BerryConfig.theme_color = background;
    this.themeService.theme.set(background);
    BerryConfig.save();
  }

  setMenuOrientation(layout: string) {
    this.layout = layout;
    document.querySelector('.coded-navbar')?.classList.remove('compact');
    document.querySelector('.coded-navbar')?.classList.remove('horizontal');
    document.querySelector('.coded-navbar')?.classList.remove('vertical');
    document.querySelector('.coded-navbar')?.classList.add(layout);
    this.themeService.themeLayout.set(layout);
  }
}
